import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, ApiError } from './client';
import type {
  AccessManifest,
  AdminMetricsSnapshot,
  ApplyResult,
  AuditEvent,
  CreateBindingRequest,
  CreateBindingResponse,
  DisablePrincipalResponse,
  Discovery,
  FeishuEventDetail,
  Health,
  IngressApplyPlan,
  IngressApplyRequest,
  Jwks,
  ListBindingsResponse,
  ListFeishuEventsResponse,
  ListGroupPollingRunsResponse,
  ListPrincipalsResponse,
  LongConnStatus,
  ManifestValidationResult,
  RetryFeishuEventResponse,
} from './types';

// ─── Public, no-auth queries ───────────────────────────────────────────────

export function useHealth(): UseQueryResult<Health, ApiError> {
  return useQuery<Health, ApiError>({
    queryKey: ['health'],
    queryFn: () => apiFetch<Health>('/healthz'),
    staleTime: 30_000,
  });
}

export function useJwks(): UseQueryResult<Jwks, ApiError> {
  return useQuery<Jwks, ApiError>({
    queryKey: ['jwks'],
    queryFn: () => apiFetch<Jwks>('/.well-known/jwks.json'),
    staleTime: 60_000,
  });
}

export function useDiscovery(): UseQueryResult<Discovery, ApiError> {
  return useQuery<Discovery, ApiError>({
    queryKey: ['discovery'],
    queryFn: () => apiFetch<Discovery>('/.well-known/openid-configuration'),
    staleTime: 60_000,
  });
}

// ─── Auth probe ────────────────────────────────────────────────────────────
/**
 * Cheap session probe. Fires `GET /api/v1/audit/events?limit=1`:
 *   - 200 → logged in AND has platform_admin
 *   - 401 → no/expired session → /login
 *   - 403 → logged in but missing platform_admin → NoAccessPage
 */
export function useAuditProbe(): UseQueryResult<AuditEvent[], ApiError> {
  return useQuery<AuditEvent[], ApiError>({
    queryKey: ['audit-probe'],
    queryFn: () => apiFetch<AuditEvent[]>('/api/v1/audit/events?limit=1'),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

// ─── Authenticated queries ─────────────────────────────────────────────────

export function useAuditEvents(limit: number): UseQueryResult<AuditEvent[], ApiError> {
  return useQuery<AuditEvent[], ApiError>({
    queryKey: ['audit-events', limit],
    queryFn: () =>
      apiFetch<AuditEvent[]>(`/api/v1/audit/events?limit=${encodeURIComponent(String(limit))}`),
    staleTime: 5_000,
  });
}

export interface FeishuEventsParams {
  status?: string;
  event_type?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export function useFeishuEvents(params: FeishuEventsParams): UseQueryResult<ListFeishuEventsResponse, ApiError> {
  return useQuery<ListFeishuEventsResponse, ApiError>({
    queryKey: ['admin-feishu-events', params],
    queryFn: () => apiFetch<ListFeishuEventsResponse>(`/api/v1/admin/feishu/events${queryString({ ...params })}`),
    staleTime: 5_000,
  });
}

export function useFeishuEventDetail(eventId: string | undefined): UseQueryResult<FeishuEventDetail, ApiError> {
  return useQuery<FeishuEventDetail, ApiError>({
    queryKey: ['admin-feishu-event', eventId],
    queryFn: () => apiFetch<FeishuEventDetail>(`/api/v1/admin/feishu/events/${encodeURIComponent(eventId ?? '')}`),
    enabled: Boolean(eventId),
  });
}

export function useLongConnStatus(): UseQueryResult<LongConnStatus, ApiError> {
  return useQuery<LongConnStatus, ApiError>({
    queryKey: ['admin-longconn-status'],
    queryFn: () => apiFetch<LongConnStatus>('/api/v1/admin/longconn/status'),
    refetchInterval: 10_000,
  });
}

export function useHealthPolling(): UseQueryResult<Health, ApiError> {
  return useQuery<Health, ApiError>({
    queryKey: ['health-polling'],
    queryFn: () => apiFetch<Health>('/healthz'),
    refetchInterval: 10_000,
  });
}

export function useGroupPollingRuns(limit: number): UseQueryResult<ListGroupPollingRunsResponse, ApiError> {
  return useQuery<ListGroupPollingRunsResponse, ApiError>({
    queryKey: ['admin-group-polling-runs', limit],
    queryFn: () => apiFetch<ListGroupPollingRunsResponse>(`/api/v1/admin/group-polling/runs?limit=${encodeURIComponent(String(limit))}`),
  });
}

export function useBindings(params: { tenant_key?: string; application_id?: string }): UseQueryResult<ListBindingsResponse, ApiError> {
  const enabled = Boolean(params.tenant_key || params.application_id);
  return useQuery<ListBindingsResponse, ApiError>({
    queryKey: ['admin-bindings', params],
    queryFn: () => apiFetch<ListBindingsResponse>(`/api/v1/admin/bindings${queryString(params)}`),
    enabled,
  });
}

export function usePrincipals(params: { tenant_key?: string; q?: string; limit?: number; offset?: number }): UseQueryResult<ListPrincipalsResponse, ApiError> {
  return useQuery<ListPrincipalsResponse, ApiError>({
    queryKey: ['admin-principals', params],
    queryFn: () => apiFetch<ListPrincipalsResponse>(`/api/v1/admin/principals${queryString(params)}`),
  });
}

export function useAdminMetrics(): UseQueryResult<AdminMetricsSnapshot, ApiError> {
  return useQuery<AdminMetricsSnapshot, ApiError>({
    queryKey: ['admin-metrics'],
    queryFn: () => apiFetch<AdminMetricsSnapshot>('/api/v1/admin/metrics'),
    refetchInterval: 10_000,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useValidateManifest(): UseMutationResult<
  ManifestValidationResult,
  ApiError,
  string
> {
  return useMutation<ManifestValidationResult, ApiError, string>({
    mutationFn: (yaml) =>
      apiFetch<ManifestValidationResult>('/api/v1/access-manifests/validate', {
        method: 'POST',
        body: yaml,
        contentType: 'application/x-yaml',
      }),
  });
}

export function useApplyManifest(): UseMutationResult<ApplyResult, ApiError, string> {
  return useMutation<ApplyResult, ApiError, string>({
    mutationFn: (yaml) =>
      apiFetch<ApplyResult>('/api/v1/access-manifests/apply', {
        method: 'POST',
        body: yaml,
        contentType: 'application/x-yaml',
      }),
  });
}

export function useIngressPlan(): UseMutationResult<
  IngressApplyPlan,
  ApiError,
  IngressApplyRequest
> {
  return useMutation<IngressApplyPlan, ApiError, IngressApplyRequest>({
    mutationFn: (request) =>
      apiFetch<IngressApplyPlan>('/api/v1/ingress/apply-plan', {
        method: 'POST',
        body: request,
      }),
  });
}

export function useValidateAuditEvent(): UseMutationResult<AuditEvent, ApiError, AuditEvent> {
  return useMutation<AuditEvent, ApiError, AuditEvent>({
    mutationFn: (event) =>
      apiFetch<AuditEvent>('/api/v1/audit/events/validate', {
        method: 'POST',
        body: event,
      }),
  });
}

export function useRetryFeishuEvent(): UseMutationResult<RetryFeishuEventResponse, ApiError, string> {
  return useMutation<RetryFeishuEventResponse, ApiError, string>({
    mutationFn: (eventId) => apiFetch<RetryFeishuEventResponse>(`/api/v1/admin/feishu/events/${encodeURIComponent(eventId)}/retry`, { method: 'POST' }),
  });
}

export function useCreateBinding(): UseMutationResult<CreateBindingResponse, ApiError, CreateBindingRequest> {
  return useMutation<CreateBindingResponse, ApiError, CreateBindingRequest>({
    mutationFn: (request) => apiFetch<CreateBindingResponse>('/api/v1/admin/bindings', { method: 'POST', body: request }),
  });
}

export function useDisablePrincipal(): UseMutationResult<DisablePrincipalResponse, ApiError, { id: string; reason: string }> {
  return useMutation<DisablePrincipalResponse, ApiError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => apiFetch<DisablePrincipalResponse>(`/api/v1/admin/principals/${encodeURIComponent(id)}/disable`, { method: 'POST', body: { reason } }),
  });
}

// Re-export so callers can `import { ApiError } from '@/api/hooks'` if they prefer.
export type { AccessManifest };
export { ApiError };
